import { ActivityInterface } from '../../interfaces/manage-activity-logs.interface';
import { Constants } from '../../constants/manage-activity-logs.constants';

export interface ActivityLogFilterInterface {
  filter(logs: ActivityInterface[]): ActivityInterface[];
}

export class ActivityCategoryFilter implements ActivityLogFilterInterface {
  constructor(public readonly categories: string[] = []) {}

  filter(logs: ActivityInterface[] = []) {
    return logs.filter(({ category }) => this.categories.includes(category));
  }
}

export class ActivityFeatureFilter implements ActivityLogFilterInterface {
  constructor(public readonly features: string[] = []) {}

  filter(logs: ActivityInterface[] = []) {
    return logs.filter((log) => this.features.includes(log.feature));
  }
}

export class ActivityMessageFilter implements ActivityLogFilterInterface {
  private message: string;

  constructor(msg: string = '') {
    this.message = msg.toLowerCase();
  }

  public filter(logs: ActivityInterface[] = []) {
    return logs.filter((log) =>
      log.message.toLowerCase().includes(this.message)
    );
  }
}

export class ActivitySurveyStatusFilter implements ActivityLogFilterInterface {
  constructor(public readonly status: number[] = []) {}

  public filter(logs: ActivityInterface[] = []) {
    return logs.filter((log) => this.status.includes(log.survey_status));
  }
}

export class ActivityTimeFilter implements ActivityLogFilterInterface {
  private time: number;

  constructor(
    public readonly date: string,
    public readonly type?: 'gte' | 'lte'
  ) {
    this.time = Date.parse(date);
  }

  public static start(date: string = '') {
    return new ActivityTimeFilter(date, 'gte');
  }

  public static end(date: string = '') {
    return new ActivityTimeFilter(date, 'lte');
  }

  public filter(logs: ActivityInterface[] = []) {
    return logs.filter((log) => {
      return this.type === 'gte'
        ? log.timestamp >= this.time
        : log.timestamp <= this.time;
    });
  }
}

export class ActivityUserFilter implements ActivityLogFilterInterface {
  constructor(public readonly users: string[] = []) {}

  filter(logs: ActivityInterface[] = []) {
    const names = this.users.map((user) => this._getUsername(user));

    return logs.filter(
      (log) =>
        names.includes(log.username) ||
        names.includes(this._getUsername(log.user_email))
    );
  }

  private _getUsername(email: string = '') {
    return email.split('@')[0];
  }
}

export class ActivityUserTypeFilter implements ActivityLogFilterInterface {
  private userTypeValues = Object.values(Constants.USER_TYPES);

  constructor(public userTypes: string[] = []) {}

  public filter(logs: ActivityInterface[] = []) {
    if (this.userTypes.length === this.userTypeValues.length) {
      return logs;
    }

    const conditions = [
      {
        selected: this.userTypes.includes(Constants.USER_TYPES.API),
        includes: (username: string) =>
          username.toLowerCase().includes(Constants.USER_TYPES.API),
      },
      {
        selected: this.userTypes.includes(Constants.USER_TYPES.PLATFORM),
        includes: (username: string) =>
          username.toLowerCase().includes(Constants.USER_TYPES.PLATFORM),
      },
    ];

    if (this.userTypes.includes(Constants.USER_TYPES.USER)) {
      const filters = conditions.filter((condition) => !condition.selected);
      return logs.filter(
        (log) => !filters.some((filter) => filter.includes(log.username))
      );
    }

    const filters = conditions.filter((condition) => condition.selected);
    return logs.filter((log) =>
      filters.some((filter) => filter.includes(log.username))
    );
  }
}
